import { computed, inject, provide, ref, type ComputedRef, type InjectionKey, type Ref } from 'vue';
import { DEFAULT_TOURISTS, type SearchCriteriaHistory, type TouristsSelection } from '@/entities/searchCriteria';
import { normalizeDateFrom, useDateFrom, type UseDateFrom } from '@/features/searchForm/dateFrom';
import type { DestinationPrices, Selection } from '@/features/searchForm/destination';
import { formatNightsRange, normalizeRange } from '@/features/searchForm/nightsRange';
import {
  CHILD_AGE_OPTIONS,
  addChild,
  formatRoomsLabel,
  formatTouristsValue,
  totalTourists,
  type ChildAgeOption,
} from '@/features/searchForm/touristsRooms';
import type { Country, DateRange, DepartureCity, NightsRange } from '@/shared/model';
import { type SearchFormCriteria, type SearchFormMobileTexts, type SearchFormTexts } from './types';
import { useCrossFieldCriteria } from './useCrossFieldCriteria';
import { useSearchFormData, type SearchFormDataUrl } from './useSearchFormData';
import { SEARCH_FORM_MOBILE_TEXTS, SEARCH_FORM_TEXTS } from '../config/texts';
import { destinationSummary } from '../lib/destinationSummary';

export type MobileScreen = 'main' | 'departure' | 'destination' | 'history' | 'menu' | 'account';

export type MobileStep = 'date' | 'nights' | 'tourists';

export type MobileEditField = 'departure' | 'destination' | 'date' | 'nights' | 'tourists';

export interface AgePickerTarget {
  roomIndex: number;
}

const DEFAULT_NIGHTS: NightsRange = { from: 7, to: 10 };

export interface MobileFlowNav {
  open: (screen: MobileScreen) => void;
  back: () => void;
  replace?: (screen: MobileScreen) => void;
}

export interface UseMobileSearchFlowOptions {
  modelValue: Ref<SearchFormCriteria>;
  formUrl: SearchFormDataUrl;
  prices: Ref<DestinationPrices | undefined>;
  isVisibleDeparture: Ref<boolean>;
  isVisibleRoomButton: Ref<boolean>;
  historySearch?: Ref<SearchCriteriaHistory[]>;
  embedded: boolean;
  nav: MobileFlowNav;
  onUpdate: (value: SearchFormCriteria) => void;
  onSearch: () => void;
  onBack?: () => void;
  searchBtnText?: Ref<string | undefined>;
  dateFieldLabel?: Ref<string | undefined>;
  nightsFieldLabel?: Ref<string | undefined>;
}

export interface MobileSearchFlowContext {
  embedded: boolean;
  isVisibleDeparture: Ref<boolean>;
  isVisibleRoomButton: Ref<boolean>;

  fromCity: Ref<DepartureCity | null>;
  destination: Ref<Selection | null>;
  nights: Ref<NightsRange>;
  tourists: Ref<TouristsSelection>;
  cities: Ref<DepartureCity[]>;
  countries: Ref<Country[]>;
  prices: Ref<DestinationPrices | undefined>;
  historySearch: Ref<SearchCriteriaHistory[]>;
  hasSwitchedCountry: Ref<boolean>;
  markSwitchedCountry: () => void;

  dateFrom: UseDateFrom;

  departureValue: ComputedRef<string>;
  destinationValue: ComputedRef<string>;
  dateValue: ComputedRef<string>;
  nightsValue: ComputedRef<string>;
  touristsValue: ComputedRef<string>;

  openStep: Ref<MobileStep | null>;
  isDateStep: ComputedRef<boolean>;
  primaryText: ComputedRef<string>;
  toggleStep: (step: MobileStep, next: boolean) => void;
  onPrimary: () => void;
  editField: (field: MobileEditField) => void;
  openEditor: (screen: MobileScreen) => void;
  submit: () => void;

  isCalendarOpen: Ref<boolean>;
  openCalendar: () => void;
  closeCalendar: () => void;

  isNightsOpen: Ref<boolean>;
  openNights: () => void;
  closeNights: () => void;

  isTouristsOpen: Ref<boolean>;
  openTourists: () => void;
  closeTourists: () => void;

  childAgeOptions: ChildAgeOption[];
  isAgePickerOpen: Ref<boolean>;
  agePickerTarget: Ref<AgePickerTarget | null>;
  openAgePicker: (target: AgePickerTarget) => void;
  closeAgePicker: () => void;
  applyChildAge: (age: number) => void;

  onDateClick: (payload: unknown) => void;
  onFlexSelect: (value: number) => void;
  onManualNights: () => void;
  updateNights: (range: NightsRange) => void;
  updateTourists: (tourists: TouristsSelection) => void;
  updateFromCity: (city: DepartureCity | null) => void;
  updateDestination: (value: Selection) => void;
  onDepartureSelect: () => void;
  onDestinationNext: () => void;
  onCalendarNext: () => void;
  onNightsNext: () => void;
  onDateNext: () => void;
  emitBack: () => void;

  open: (screen: MobileScreen) => void;
  back: () => void;

  texts: SearchFormMobileTexts;
  primaryTexts: SearchFormTexts;
}

export const MOBILE_SEARCH_FLOW: InjectionKey<MobileSearchFlowContext> = Symbol('mobile-search-flow');

export function useMobileSearchFlow(options: UseMobileSearchFlowOptions): MobileSearchFlowContext {
  const model = options.modelValue;
  const openStep = ref<MobileStep | null>(null);
  const isCalendarOpen = ref<boolean>(false);
  const isNightsOpen = ref<boolean>(false);
  const isTouristsOpen = ref<boolean>(false);
  const isAgePickerOpen = ref<boolean>(false);
  const agePickerTarget = ref<AgePickerTarget | null>(null);

  const { countries, cities, defaultCriteria } = useSearchFormData(options.formUrl);

  const update = (patch: Partial<SearchFormCriteria>): void => {
    options.onUpdate({ ...model.value, ...patch });
  };

  const destination = computed<Selection | null>(() => model.value.destination);
  const nights = computed<NightsRange>({
    get: () => model.value.nights ?? DEFAULT_NIGHTS,
    set: (value) => update({ nights: value }),
  });
  const dateRange = computed<DateRange | null>({
    get: () => model.value.dateRange,
    set: (value) => update({ dateRange: value }),
  });
  const tourists = computed<TouristsSelection>({
    get: () => model.value.tourists ?? DEFAULT_TOURISTS,
    set: (value) => update({ tourists: value }),
  });

  const criteria = useCrossFieldCriteria({ countries, defaultCriteria, destination, nights, dateRange });

  const fromCity = computed<DepartureCity | null>({
    get: () => model.value.fromCity,
    set: (value) => {
      options.onUpdate({ ...model.value, fromCity: value, ...criteria.resolveDefaults(value, destination.value) });
    },
  });

  const dateModel = computed<DateRange>({
    get: () => normalizeDateFrom(dateRange.value),
    set: (value) => {
      dateRange.value = value;
    },
  });
  const dateFrom = useDateFrom({ model: dateModel });

  const dateLabel = computed<string>(() => options.dateFieldLabel?.value || SEARCH_FORM_MOBILE_TEXTS.labels.date);
  const nightsLabel = computed<string>(() => options.nightsFieldLabel?.value || SEARCH_FORM_MOBILE_TEXTS.labels.nights);
  const searchBtnLabel = computed<string>(() => options.searchBtnText?.value || SEARCH_FORM_TEXTS.searchBtn);
  const texts: SearchFormMobileTexts = {
    ...SEARCH_FORM_MOBILE_TEXTS,
    labels: {
      ...SEARCH_FORM_MOBILE_TEXTS.labels,
      get date(): string {
        return dateLabel.value;
      },
      get nights(): string {
        return nightsLabel.value;
      },
    },
  };

  const primaryTexts: SearchFormTexts = {
    ...SEARCH_FORM_TEXTS,
    get searchBtn(): string {
      return searchBtnLabel.value;
    },
  };

  const departureValue = computed<string>(() => fromCity.value?.name ?? '');
  const destinationValue = computed<string>(() => destinationSummary(destination.value));
  const dateValue = computed<string>(() => (dateRange.value ? dateFrom.valueLabel.value : ''));
  const nightsValue = computed<string>(() => {
    const range = normalizeRange(nights.value);
    return formatNightsRange(range.from, range.to, '—');
  });
  const touristsValue = computed<string>(() => {
    const rooms = tourists.value.rooms;
    const roomsText = options.isVisibleRoomButton.value
      ? formatRoomsLabel(rooms.length, options.isVisibleRoomButton.value)
      : '';
    return `${roomsText} ${formatTouristsValue(
      totalTourists(tourists.value),
      options.isVisibleRoomButton.value,
      rooms,
    )}`;
  });

  const isDateStep = computed<boolean>(() => openStep.value === 'date');
  const primaryText = computed<string>(() =>
    !options.embedded && isDateStep.value ? SEARCH_FORM_TEXTS.next : searchBtnLabel.value,
  );

  const toggleStep = (step: MobileStep, next: boolean): void => {
    openStep.value = next ? step : null;
  };

  const onPrimary = (): void => {
    if (!options.embedded && isDateStep.value) {
      openStep.value = 'nights';
      return;
    }
    options.onSearch();
  };

  const onDateClick = (payload: unknown): void => {
    const iso =
      typeof payload === 'string'
        ? payload
        : payload && typeof payload === 'object' && 'start' in payload
        ? (payload as { start?: string }).start
        : undefined;
    if (!iso) {
      return;
    }
    dateFrom.setAnchorFromISO(iso);
    criteria.markTouchedDate();
  };

  const onFlexSelect = (value: number): void => {
    dateFrom.setFlexible(value);
    criteria.markTouchedDate();
  };

  const onManualNights = (): void => {
    criteria.markTouchedNights();
  };

  const updateNights = (range: NightsRange): void => {
    nights.value = range;
  };

  const updateTourists = (next: TouristsSelection): void => {
    tourists.value = next;
  };

  const updateFromCity = (city: DepartureCity | null): void => {
    fromCity.value = city;
  };

  const updateDestination = (value: Selection): void => {
    options.onUpdate({
      ...model.value,
      destination: value,
      ...criteria.resolveDefaults(fromCity.value, value),
    });
  };

  const onDepartureSelect = (): void => {
    options.nav.back();
  };

  const onDestinationNext = (): void => {
    options.nav.back();
  };

  const openCalendar = (): void => {
    isCalendarOpen.value = true;
  };

  const closeCalendar = (): void => {
    isCalendarOpen.value = false;
  };

  const onCalendarNext = (): void => {
    closeCalendar();
  };

  const openNights = (): void => {
    isNightsOpen.value = true;
  };

  const closeNights = (): void => {
    isNightsOpen.value = false;
  };

  const onNightsNext = (): void => {
    closeNights();
  };

  const openTourists = (): void => {
    isTouristsOpen.value = true;
  };

  const closeTourists = (): void => {
    isTouristsOpen.value = false;
  };

  const openAgePicker = (target: AgePickerTarget): void => {
    agePickerTarget.value = target;
    isAgePickerOpen.value = true;
  };

  const closeAgePicker = (): void => {
    isAgePickerOpen.value = false;
    agePickerTarget.value = null;
  };

  const applyChildAge = (age: number): void => {
    const target = agePickerTarget.value;
    if (!target) {
      return;
    }
    tourists.value = addChild(tourists.value, target.roomIndex, age);
  };

  const onDateNext = (): void => {
    openStep.value = 'nights';
  };

  const openEditor = (screen: MobileScreen): void => {
    options.nav.open(screen);
  };

  const editField = (field: MobileEditField): void => {
    if (field === 'departure' || field === 'destination') {
      openStep.value = null;
      options.nav.open(field);
      return;
    }
    if (field === 'date') {
      openCalendar();
      return;
    }
    if (field === 'nights') {
      openNights();
      return;
    }
    openTourists();
  };

  const submit = (): void => {
    options.onSearch();
  };

  const hasSwitchedCountry = ref(false);
  const markSwitchedCountry = (): void => {
    hasSwitchedCountry.value = true;
  };

  return {
    embedded: options.embedded,
    isVisibleDeparture: options.isVisibleDeparture,
    isVisibleRoomButton: options.isVisibleRoomButton,

    fromCity,
    destination,
    nights,
    tourists,
    cities,
    countries,
    prices: options.prices,
    historySearch: options.historySearch ?? ref<SearchCriteriaHistory[]>([]),
    hasSwitchedCountry,
    markSwitchedCountry,

    dateFrom,

    departureValue,
    destinationValue,
    dateValue,
    nightsValue,
    touristsValue,

    openStep,
    isDateStep,
    primaryText,
    toggleStep,
    onPrimary,
    editField,
    openEditor,
    submit,

    isCalendarOpen,
    openCalendar,
    closeCalendar,

    isNightsOpen,
    openNights,
    closeNights,

    isTouristsOpen,
    openTourists,
    closeTourists,

    childAgeOptions: CHILD_AGE_OPTIONS,
    isAgePickerOpen,
    agePickerTarget,
    openAgePicker,
    closeAgePicker,
    applyChildAge,

    onDateClick,
    onFlexSelect,
    onManualNights,
    updateNights,
    updateTourists,
    updateFromCity,
    updateDestination,
    onDepartureSelect,
    onDestinationNext,
    onCalendarNext,
    onNightsNext,
    onDateNext,
    emitBack: options.onBack ?? ((): void => undefined),

    open: options.nav.open,
    back: options.nav.back,

    texts,
    primaryTexts,
  };
}

export function provideMobileSearchFlow(context: MobileSearchFlowContext): void {
  provide(MOBILE_SEARCH_FLOW, context);
}

export function useMobileSearchFlowContext(): MobileSearchFlowContext {
  const context = inject(MOBILE_SEARCH_FLOW, null);
  if (!context) {
    throw new Error('useMobileSearchFlowContext: контекст мобильного flow не предоставлен хостом');
  }
  return context;
}
